import { useQuery } from '@apollo/client';
import { Box } from '@zoopla-eng/zds-components/box';
import { TickMedium, ArrowRightSmall } from '@zoopla-eng/zds-components/icons';
import ListingCard, {
    getAmenities,
    getDetailsLink,
    getListingImage
} from '@zoopla/listing-card';
import {
    AnalyticsTaxonomy,
    SearchCategory,
    SearchSource
} from '@zoopla/listing-search-context';
import {
    DevelopmentDetailsQuery,
    DevelopmentDetailsQueryVariables,
    DEVELOPMENT_DETAILS_QUERY
} from '@zoopla/listing-search-queries';
import {
    segmentSearchResultClickEvent,
    segmentSearchResultImpressionEvent
} from '@zoopla/listing-search/segment';
import { FiltersTrackingObj } from '@zoopla/listing-search-utils';
import { useEffect } from 'react';
import { DraggableList } from './DraggableList';
import {
    amenitiesContainerStyle,
    containerStyle,
    developmentInfoCardStyle,
    developmentInfoCardListStyle,
    developmentInfoCardListItemStyle,
    developmentInfoCardTitleStyle,
    draggableStyle,
    linkContentStyle,
    linkWrapperStyle,
    listItemStyle,
    detailsPageLinkStyle,
    descriptionStyle,
    developmentInfoLinkStyle
} from './NewHomesListings.styles.css';
import {
    developmentTracking,
    ecommerceClickTracking,
    ecommerceImpressionTracking
} from './NewHomesListings.tracking';
import { NewHomesListingSkeleton } from './NewHomesListingSkeleton';
import { getDevelopmentInfo, mapDevelopmentDetailsToListings } from './utils';
import { LinkWithIcon } from '@zoopla-eng/zds-components/link';

import { fontGroups } from '@zoopla-eng/zds-components/themes';

export type NewHomesListingsProps = {
    parentListingId: string;
    branchId: string | number;
    searchIdentifier: string;
    analyticsTaxonomy?: AnalyticsTaxonomy;
    searchSource: SearchSource;
    searchCategory: SearchCategory;
    filters: FiltersTrackingObj;
    pageNumber: number;
    developmentUrl?: string | null;
};

export const NewHomesListings = ({
    parentListingId,
    branchId,
    searchIdentifier,
    analyticsTaxonomy,
    searchSource,
    searchCategory,
    filters,
    pageNumber,
    developmentUrl
}: NewHomesListingsProps) => {
    const { data, loading } = useQuery<
        DevelopmentDetailsQuery,
        DevelopmentDetailsQueryVariables
    >(DEVELOPMENT_DETAILS_QUERY, {
        variables: {
            id: Number(branchId),
            deprecatedBranchId: Number(branchId)
        }
    });

    const developmentName = data?.newHomesDevelopmentData?.name;
    const listings = mapDevelopmentDetailsToListings(data);

    useEffect(() => {
        if (listings) {
            ecommerceImpressionTracking({
                ecommerce: {
                    currencyCode: 'GBP',
                    impressions: listings.map(({ listingId }, index) => ({
                        __typename: 'AnalyticsEcommerceImpressions',
                        id: listingId,
                        list: 'development listings carousel',
                        position: index + 1,
                        variant: 'premium'
                    }))
                }
            });
        }
    }, [listings]);

    if (loading) {
        return <NewHomesListingSkeleton />;
    }

    return (
        <div className={containerStyle}>
            {developmentName && developmentUrl && (
                <div className={`${linkWrapperStyle} ${fontGroups.knife}`}>
                    <LinkWithIcon
                        href={developmentUrl}
                        title={`See all available properties at ${developmentName}`}
                        iconSuffix={<ArrowRightSmall />}
                        onClick={developmentTracking}
                    >
                        See all available properties at {developmentName}
                    </LinkWithIcon>
                </div>
            )}
            <DraggableList aria-label="All available properties">
                {listings && (
                    <li
                        className={developmentInfoCardStyle}
                        key={`development-details-listing-${branchId}`}
                    >
                        <div className={draggableStyle} draggable="false">
                            <a
                                className={developmentInfoLinkStyle}
                                draggable="false"
                                href={developmentUrl ?? ''}
                                title={`See all available properties at ${developmentName}`}
                            >
                                <h3
                                    className={`${developmentInfoCardTitleStyle} ${fontGroups.fork}`}
                                    key={`development-details-title-${branchId}`}
                                >
                                    Available in this development
                                </h3>
                                <ul
                                    className={`${developmentInfoCardListStyle} ${fontGroups.fork}`}
                                    key={`development-details-attributes-${branchId}`}
                                >
                                    {getDevelopmentInfo(data).map(
                                        (attribute) => (
                                            <li
                                                key={`development-info-card-list-item-${attribute}`}
                                                className={
                                                    developmentInfoCardListItemStyle
                                                }
                                            >
                                                <TickMedium /> {attribute}
                                            </li>
                                        )
                                    )}
                                </ul>
                            </a>
                        </div>
                    </li>
                )}
                {listings &&
                    listings.map((listing, index) => {
                        const detailsLink = getDetailsLink(
                            searchIdentifier,
                            listing.listingUris,
                            parentListingId
                        );

                        const { price, listingId } = listing;

                        return (
                            <li
                                className={listItemStyle}
                                key={`development-details-listing-${branchId}-${index}`}
                            >
                                <ListingCard.InView
                                    onInView={() => {
                                        if (!analyticsTaxonomy) return;
                                        if (!listingId) return;

                                        segmentSearchResultImpressionEvent(
                                            analyticsTaxonomy,
                                            searchSource,
                                            searchCategory,
                                            filters,
                                            listingId,
                                            price ?? '',
                                            pageNumber
                                        );
                                    }}
                                >
                                    <ListingCard.LayoutLandscapeMini>
                                        <ListingCard.LayoutMedia
                                            onClick={() => {
                                                if (!analyticsTaxonomy) return;
                                                if (!listingId) return;

                                                ecommerceClickTracking({
                                                    id: Number(listingId),
                                                    variant: 'premium',
                                                    position: index + 1,
                                                    list: 'development listings carousel'
                                                });

                                                segmentSearchResultClickEvent(
                                                    analyticsTaxonomy,
                                                    searchSource,
                                                    searchCategory,
                                                    filters,
                                                    listingId,
                                                    price ?? '',
                                                    pageNumber
                                                );
                                            }}
                                        >
                                            <a
                                                className={detailsPageLinkStyle}
                                                href={detailsLink}
                                                draggable="false"
                                                aria-label={`link to listing ${listingId}`}
                                            >
                                                <ListingCard.Image
                                                    {...getListingImage(
                                                        listing
                                                    )}
                                                    draggable={false}
                                                />
                                            </a>
                                        </ListingCard.LayoutMedia>
                                        <ListingCard.LayoutContent
                                            onClick={() => {
                                                if (!analyticsTaxonomy) return;
                                                if (!listingId) return;

                                                ecommerceClickTracking(
                                                    {
                                                        id: Number(listingId),
                                                        variant: 'premium',
                                                        position: index + 1,
                                                        list: 'development listings carousel'
                                                    },
                                                    'development listings carousel'
                                                );

                                                segmentSearchResultClickEvent(
                                                    analyticsTaxonomy,
                                                    searchSource,
                                                    searchCategory,
                                                    filters,
                                                    listingId,
                                                    price ?? '',
                                                    pageNumber
                                                );
                                            }}
                                        >
                                            <Box pt="S200" height="100%">
                                                <a
                                                    className={
                                                        detailsPageLinkStyle
                                                    }
                                                    href={detailsLink}
                                                    aria-label={`link to listing ${listingId}`}
                                                >
                                                    <div
                                                        className={
                                                            linkContentStyle
                                                        }
                                                    >
                                                        <ListingCard.Price
                                                            priceAlternative={
                                                                listing.price
                                                            }
                                                        />
                                                        <p
                                                            className={`${descriptionStyle} ${fontGroups.spoon}`}
                                                        >
                                                            {
                                                                listing.description
                                                            }
                                                        </p>
                                                        <div
                                                            className={
                                                                amenitiesContainerStyle
                                                            }
                                                        >
                                                            <ListingCard.Amenities
                                                                {...getAmenities(
                                                                    listing
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                </a>
                                            </Box>
                                        </ListingCard.LayoutContent>
                                    </ListingCard.LayoutLandscapeMini>
                                </ListingCard.InView>
                            </li>
                        );
                    })}
            </DraggableList>
        </div>
    );
};
